import React, { useEffect, useState } from "react";
import { Collapse, Button } from "react-bootstrap";
import { ActionHandlerFunction, IActionHandler } from "@preveil-api";
import { AdminUIActionTypes, AdminErrorMessages, useCsvDataValidation, FileTemplates, CSVUploadStatusHeading, FileUploadTypes } from "src/common";
import { DraggableContainer, Icon } from "src/components";
import { AddressPanel } from "..";
import _ from "lodash";
import { ParseError, ParseMeta } from "papaparse";

type AllProps = {
  handleAction: ActionHandlerFunction;
  address_list: string[];
  submitAddresses: Function;
}

type PapaparseData = {
  data: {
    "Domain or User Address": string
  }
}

function GroupFormComponent(props: AllProps) {
  const { handleAction, address_list, submitAddresses } = props;
  const csv_column_name = "Domain or User Address"; // column name that matches the document template provided to the user.
  const [open_collapse, setOpenCollapse] = useState<boolean>(false);
  const { valid_addresses, invalid_addresses, duplicate_addresses, is_data_drop, handleValidateData, resetData } = useCsvDataValidation(address_list);

  useEffect(() => {
    return () => {  
      resetData();
    };
  }, []);

  const GroupFormRequests = {
    // Handle change for individual addresses/domain tab
    handleSubmitAddresses: (collection?: string[]) => {
      submitAddresses(collection);
    },
    // Handle change when using CSV Upload
    handleSubmitCsvData: () => {
      resetData();
      submitAddresses(valid_addresses);
    },
    handleReset: () => {
      resetData();
    },
    handleShowModal: () => {
      handleAction({ actionType: AdminUIActionTypes.ShowModal });
      resetData();
    },
    handleCsvData: (csv_file: { data: PapaparseData[], errors: ParseError[], meta: ParseMeta }) => {
      handleCsvFileData(csv_file);
    },
    // Handle error from Draggable component (wrong file extension).
    handlePageError: (params: { message: string, stack: string }) => {
      const { message, stack } = params;
      handleAction({ actionType: AdminUIActionTypes.PageError, params: { message, stack } });
    }
  };

  function handlePageActions(action_obj: IActionHandler) {
    const callback = `handle${action_obj.actionType}`;
    if ((GroupFormRequests as any)[callback] instanceof Function) {
      (GroupFormRequests as any)[callback](action_obj.params);
    }
  }

  // handling csv data.
  function handleCsvFileData(csv_file: { data: PapaparseData[], errors: ParseError[], meta: ParseMeta }) {
    if (csv_file?.errors && csv_file?.errors.length >= 1) {
      // Papaparse returns some errors when the CSV formatting is not correct
      // or the data is missing a delimiter in each row, but that doesn't mean the data will not be parse.
      // so we are just logging those errors.
      handleAction({ actionType: AdminUIActionTypes.PageErrorMessage, params: { message: AdminErrorMessages.error_csv_upload_format, stack: csv_file.errors } });
    }
    const { data, meta } = csv_file;
    const is_valid_columns = meta.fields?.includes(csv_column_name); // we need to verify if the columns name inside the csv file matches the one on the csv template.
    if (data.length === 0 || !is_valid_columns) {
      if (data.length === 0) {
        handleAction({ actionType: AdminUIActionTypes.PageError, params: { message: AdminErrorMessages.error_csv_empty_file, stack: data } });
      } else if (!is_valid_columns) {
        handleAction({ actionType: AdminUIActionTypes.PageError, params: { message: AdminErrorMessages.error_csv_wrong_heading, stack: meta.fields } });
      }
    } else {
      // any repeated address/domain from the csv file will be removed (only within the document).
      const addresses_from_csv_file: string[] = _.uniq(data.map((data_row: any) => data_row[csv_column_name].trim().toLowerCase()));
      handleValidateData(addresses_from_csv_file);
    }
  };

  // Description: Handle delete of a single valid address
  function handleDeleteAction(id: string | undefined) {
    if (!!id) {
      const data = [...valid_addresses, ...invalid_addresses, ...duplicate_addresses].filter((address: string) => address !== id);
      handleValidateData(data);
    }
  };

  return <>
    {valid_addresses.length >= 1 && is_data_drop && (
      <AddressPanel
        title={CSVUploadStatusHeading.valid_trusted}
        address_list={valid_addresses}
        handleDelete={handleDeleteAction}
      />
    )}
    {!(_.isEmpty(invalid_addresses) && _.isEmpty(duplicate_addresses)) && (
      <div className={`invalid-card ${!_.isEmpty(duplicate_addresses) ? "show-divider" : ""}`}>
        <header>
          <h3 className="mb-3">
            Aliases that will <i className="emphasis">not</i> be added
          </h3>
          <Button
            variant="icon"
            onClick={() => setOpenCollapse(!open_collapse)}
            aria-controls="collapse-invalid-area"
            >
            <Icon className={`${open_collapse ? "ficon-chevron-up" : "ficon-chevron-down"}`}/>
          </Button>
        </header>
        <Collapse in={open_collapse}>
          <div>
              {duplicate_addresses.length >= 1 && is_data_drop && (
                <AddressPanel
                  title={CSVUploadStatusHeading.duplicate_alias}
                  address_list={duplicate_addresses}
                  error={true}
                  handleDelete={handleDeleteAction}
                />
              )}
              {invalid_addresses.length >= 1 && is_data_drop && (
                <AddressPanel
                  title={CSVUploadStatusHeading.invalid}
                  address_list={invalid_addresses}
                  error={true}
                  handleDelete={handleDeleteAction}
                />
              )}
          </div>
        </Collapse>
      </div>
    )}
    {!is_data_drop && (
      <div className="mb-3">
        <p>
          Download and use the upload template to accurately add multiple users at once
          <a
            href={FileTemplates.trusted_community_users}
            target="_blank"
            rel="noreferrer">
            <Icon className="ficon-download small"/>
            PreVeil-Trusted-Community-Upload-Template.csv
          </a>
        </p>
        <DraggableContainer handleAction={handlePageActions} file_upload_type={FileUploadTypes.CSV}/>
      </div>
    )}
    <div className="btn-panel">
      <Button id="submit_button"
        onClick={() => GroupFormRequests.handleSubmitCsvData()}
        disabled={_.isEmpty(valid_addresses)}
      >
        Add to List
      </Button>
      <Button
        id="cancel_button"
        variant="no-outline-primary"
        onClick={() => handleAction({ actionType: AdminUIActionTypes.ShowModal })}
      >
        Cancel
      </Button>
    </div>
  </>;
}

export default React.memo(GroupFormComponent);
